import type {
  Novel,
  Chapter,
  ChapterContent,
  CrawlProgress,
  ChapterNav,
  ApiResponse,
  PaginatedResponse,
  ExportData,
  User,
  ParseResponse,
  UploadNovelRequest,
  UploadNovelResponse,
  UserReadingProgress,
  FavoriteNovel,
  Series,
  SeriesDetail,
  SeriesFormData,
  LLMProviderSettings,
  LLMProviderFormData,
  LLMTestResult,
  ExternalSearchResponse,
  SearchableSource,
  SyncChaptersResult,
} from './types';

const API_BASE = import.meta.env.PROD
  ? 'https://novel-api.ruei2studio.com/api'
  : '/api';

// Token refresh helper
async function refreshToken(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    return response.ok;
  } catch {
    return false;
  }
}

async function request<T>(url: string, options?: RequestInit, isRetry = false): Promise<T> {
  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers
    }
  });

  // Handle 401 by attempting token refresh (only once)
  if (response.status === 401 && !isRetry) {
    const refreshed = await refreshToken();
    if (refreshed) {
      return request<T>(url, options, true);
    }
  }

  const data: ApiResponse<T> = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Unknown error');
  }

  return data.data as T;
}

export const api = {
  // Novels
  async createNovel(url: string): Promise<Novel> {
    return request<Novel>('/novels', {
      method: 'POST',
      body: JSON.stringify({ url })
    });
  },

  async getNovels(): Promise<Novel[]> {
    return request<Novel[]>('/novels');
  },

  async getRandomNovels(limit: number = 10): Promise<Novel[]> {
    return request<Novel[]>(`/novels/random?limit=${limit}`);
  },

  async searchNovels(query: string, limit: number = 50): Promise<Novel[]> {
    return request<Novel[]>(`/novels/search?q=${encodeURIComponent(query)}&limit=${limit}`);
  },

  async getNovelsByAuthor(authorName: string): Promise<Novel[]> {
    return request<Novel[]>(`/novels/author/${encodeURIComponent(authorName)}`);
  },

  async getNovel(id: string): Promise<Novel> {
    return request<Novel>(`/novels/${id}`);
  },

  async deleteNovel(id: string): Promise<void> {
    return request<void>(`/novels/${id}`, { method: 'DELETE' });
  },

  async pauseNovel(id: string): Promise<Novel> {
    return request<Novel>(`/novels/${id}/pause`, { method: 'POST' });
  },

  async resumeNovel(id: string): Promise<Novel> {
    return request<Novel>(`/novels/${id}/resume`, { method: 'POST' });
  },

  async recrawlEmptyChapters(id: string): Promise<Novel & { recheduledCount: number }> {
    return request<Novel & { recheduledCount: number }>(`/novels/${id}/recrawl-empty`, { method: 'POST' });
  },

  async syncChapters(id: string, dryRun = true): Promise<SyncChaptersResult> {
    return request<SyncChaptersResult>(`/novels/${id}/sync-chapters`, {
      method: 'POST',
      body: JSON.stringify({ dryRun })
    });
  },

  async recrawlChapter(chapterId: string): Promise<Chapter & { message: string }> {
    return request<Chapter & { message: string }>(`/chapters/${chapterId}/recrawl`, { method: 'POST' });
  },

  async skipChapter(chapterId: string): Promise<Chapter & { message: string }> {
    return request<Chapter & { message: string }>(`/chapters/${chapterId}/skip`, { method: 'POST' });
  },

  async getNovelProgress(id: string): Promise<CrawlProgress> {
    return request<CrawlProgress>(`/novels/${id}/progress`);
  },

  // Chapters
  async getChapters(novelId: string, page: number = 1, limit: number = 50): Promise<PaginatedResponse<Chapter>> {
    return request<PaginatedResponse<Chapter>>(`/novels/${novelId}/chapters?page=${page}&limit=${limit}`);
  },

  async getAllChapters(novelId: string): Promise<Chapter[]> {
    const pageSize = 500;
    let page = 1;
    const allChapters: Chapter[] = [];

    while (true) {
      const data = await request<PaginatedResponse<Chapter>>(
        `/novels/${novelId}/chapters?page=${page}&limit=${pageSize}`
      );
      allChapters.push(...data.chapters);
      if (page >= data.pagination.totalPages) break;
      page++;
    }

    return allChapters;
  },

  async getChapter(id: string): Promise<ChapterContent> {
    return request<ChapterContent>(`/chapters/${id}`);
  },

  async markChapterRead(id: string): Promise<UserReadingProgress> {
    return request<UserReadingProgress>(`/chapters/${id}/read`, { method: 'POST' });
  },

  async getChapterNav(id: string): Promise<ChapterNav> {
    return request<ChapterNav>(`/chapters/${id}/nav`);
  },

  // 章節分析（劇情摘要）
  async getChapterAnalysis(chapterId: string): Promise<{
    hasAnalysis: boolean;
    plotSummary: string | null;
    tensionRating: number | null;
  }> {
    return request<{
      hasAnalysis: boolean;
      plotSummary: string | null;
      tensionRating: number | null;
    }>(`/chapters/${chapterId}/analysis`);
  },

  async exportNovel(novelId: string): Promise<ExportData> {
    return request<ExportData>(`/novels/${novelId}/export`);
  },

  // Admin
  async getAllUsers(): Promise<User[]> {
    return request<User[]>('/admin/users');
  },

  async getPendingUsers(): Promise<User[]> {
    return request<User[]>('/admin/users/pending');
  },

  async approveUser(userId: string): Promise<User> {
    return request<User>(`/admin/users/${userId}/approve`, { method: 'POST' });
  },

  async rejectUser(userId: string): Promise<{ rejected: boolean }> {
    return request<{ rejected: boolean }>(`/admin/users/${userId}/reject`, { method: 'POST' });
  },

  async unrejectUser(userId: string): Promise<User> {
    return request<User>(`/admin/users/${userId}/unreject`, { method: 'POST' });
  },

  async promoteUser(userId: string): Promise<User> {
    return request<User>(`/admin/users/${userId}/promote`, { method: 'POST' });
  },

  // Admin Novel Management
  async parseNovelFiles(files: File[]): Promise<ParseResponse> {
    const formData = new FormData();
    files.forEach(file => formData.append('files[]', file));

    const response = await fetch(`${API_BASE}/admin/novels/parse`, {
      method: 'POST',
      credentials: 'include',
      body: formData
    });

    const data: ApiResponse<ParseResponse> = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to parse files');
    }

    return data.data as ParseResponse;
  },

  async uploadNovel(data: UploadNovelRequest): Promise<UploadNovelResponse> {
    return request<UploadNovelResponse>('/admin/novels/upload', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  // 分批上傳 API
  async createNovelOnly(data: { title: string; author?: string; description?: string; totalChapters?: number }): Promise<Novel> {
    return request<Novel>('/admin/novels/create', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  async uploadChapter(novelId: string, data: { index: number; title: string; content: string }): Promise<{ chapterId: string; index: number; title: string; wordCount: number }> {
    return request<{ chapterId: string; index: number; title: string; wordCount: number }>(`/admin/novels/${novelId}/chapter`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  async completeNovelUpload(novelId: string): Promise<Novel> {
    return request<Novel>(`/admin/novels/${novelId}/complete`, {
      method: 'POST'
    });
  },

  async updateNovel(id: string, data: { title?: string; author?: string; description?: string; cover_url?: string }): Promise<Novel> {
    return request<Novel>(`/admin/novels/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  async addChapter(novelId: string, data: { title: string; content: string; index?: number }): Promise<Chapter> {
    return request<Chapter>(`/admin/novels/${novelId}/chapters`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  async updateChapter(id: string, data: { title: string }): Promise<Chapter> {
    return request<Chapter>(`/admin/chapters/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  async deleteChapter(id: string): Promise<void> {
    return request<void>(`/admin/chapters/${id}`, { method: 'DELETE' });
  },

  async batchDeleteChapters(novelId: string, chapterIds: string[]): Promise<{ deleted: boolean; deletedCount: number }> {
    return request<{ deleted: boolean; deletedCount: number }>(`/admin/novels/${novelId}/chapters/batch`, {
      method: 'DELETE',
      body: JSON.stringify({ chapterIds })
    });
  },

  async reorderChapters(novelId: string, chapters: Array<{ id: string; newIndex: number }>): Promise<{ reordered: boolean; chapters: Chapter[] }> {
    return request<{ reordered: boolean; chapters: Chapter[] }>(`/admin/novels/${novelId}/reorder`, {
      method: 'POST',
      body: JSON.stringify({ chapters })
    });
  },

  async getAdminNovelChapters(novelId: string): Promise<{ novel: Novel; chapters: Chapter[] }> {
    return request<{ novel: Novel; chapters: Chapter[] }>(`/admin/novels/${novelId}/chapters`);
  },

  // Favorites
  async getFavorites(): Promise<FavoriteNovel[]> {
    return request<FavoriteNovel[]>('/favorites');
  },

  async addFavorite(novelId: string): Promise<{ id: string; novel_id: string; favorited: boolean }> {
    return request<{ id: string; novel_id: string; favorited: boolean }>(`/favorites/${novelId}`, {
      method: 'POST'
    });
  },

  async removeFavorite(novelId: string): Promise<{ novel_id: string; unfavorited: boolean }> {
    return request<{ novel_id: string; unfavorited: boolean }>(`/favorites/${novelId}`, {
      method: 'DELETE'
    });
  },

  async checkFavorite(novelId: string): Promise<{ isFavorited: boolean }> {
    return request<{ isFavorited: boolean }>(`/novels/${novelId}/favorite`);
  },

  // Reading Progress
  async getAllReadingProgress(): Promise<UserReadingProgress[]> {
    return request<UserReadingProgress[]>('/reading-progress');
  },

  async getReadingProgress(novelId: string): Promise<UserReadingProgress | null> {
    return request<UserReadingProgress | null>(`/reading-progress/${novelId}`);
  },

  async updateReadingProgress(
    novelId: string,
    chapterId: string,
    progressPercentage: number = 0
  ): Promise<UserReadingProgress> {
    return request<UserReadingProgress>(`/reading-progress/${novelId}`, {
      method: 'POST',
      body: JSON.stringify({ chapterId, progressPercentage })
    });
  },

  // Novel Series
  async getSeries(): Promise<Series[]> {
    return request<Series[]>('/series');
  },

  async getSeriesDetail(id: string): Promise<SeriesDetail> {
    return request<SeriesDetail>(`/series/${id}`);
  },

  async createSeries(data: SeriesFormData): Promise<Series> {
    return request<Series>('/admin/series', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  async updateSeries(id: string, data: SeriesFormData): Promise<Series> {
    return request<Series>(`/admin/series/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  async deleteSeries(id: string): Promise<{ success: boolean }> {
    return request<{ success: boolean }>(`/admin/series/${id}`, {
      method: 'DELETE'
    });
  },

  async addNovelToSeries(seriesId: string, novelId: string): Promise<{ success: boolean; novel: Novel }> {
    return request<{ success: boolean; novel: Novel }>(`/admin/series/${seriesId}/novels`, {
      method: 'POST',
      body: JSON.stringify({ novel_id: novelId })
    });
  },

  async removeNovelFromSeries(seriesId: string, novelId: string): Promise<{ success: boolean }> {
    return request<{ success: boolean }>(`/admin/series/${seriesId}/novels/${novelId}`, {
      method: 'DELETE'
    });
  },

  async reorderSeriesNovels(seriesId: string, novelIds: string[]): Promise<{ success: boolean }> {
    return request<{ success: boolean }>(`/admin/series/${seriesId}/reorder`, {
      method: 'PUT',
      body: JSON.stringify({ novel_ids: novelIds })
    });
  },

  // LLM Provider Management
  async getLLMProviders(): Promise<LLMProviderSettings[]> {
    const result = await request<{ providers: LLMProviderSettings[] }>('/admin/llm/providers');
    return result.providers;
  },

  async getActiveLLMProvider(): Promise<LLMProviderSettings | null> {
    try {
      const result = await request<{ provider: LLMProviderSettings }>('/admin/llm/active');
      return result.provider;
    } catch {
      return null;
    }
  },

  async createLLMProvider(data: LLMProviderFormData): Promise<LLMProviderSettings> {
    const result = await request<{ provider: LLMProviderSettings }>('/admin/llm/providers', {
      method: 'POST',
      body: JSON.stringify(data)
    });
    return result.provider;
  },

  async updateLLMProvider(id: string, data: Partial<LLMProviderFormData>): Promise<LLMProviderSettings> {
    const result = await request<{ provider: LLMProviderSettings }>(`/admin/llm/providers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
    return result.provider;
  },

  async deleteLLMProvider(id: string): Promise<{ deleted: boolean }> {
    return request<{ deleted: boolean }>(`/admin/llm/providers/${id}`, {
      method: 'DELETE'
    });
  },

  async activateLLMProvider(id: string): Promise<{ activated: boolean; provider_id: string }> {
    return request<{ activated: boolean; provider_id: string }>(`/admin/llm/providers/${id}/activate`, {
      method: 'POST'
    });
  },

  async enableLLMProvider(id: string): Promise<{ enabled: boolean; provider_id: string }> {
    return request<{ enabled: boolean; provider_id: string }>(`/admin/llm/providers/${id}/enable`, {
      method: 'PUT'
    });
  },

  async disableLLMProvider(id: string): Promise<{ disabled: boolean; provider_id: string }> {
    return request<{ disabled: boolean; provider_id: string }>(`/admin/llm/providers/${id}/disable`, {
      method: 'PUT'
    });
  },

  async getEnabledLLMProviders(): Promise<LLMProviderSettings[]> {
    const result = await request<{ providers: LLMProviderSettings[] }>('/admin/llm/providers/enabled');
    return result.providers;
  },

  async testLLMProvider(id: string): Promise<LLMTestResult> {
    return request<LLMTestResult>(`/admin/llm/providers/${id}/test`, {
      method: 'POST'
    });
  },

  // External Search (跨來源搜尋)
  async searchExternal(query: string, limit: number = 20): Promise<ExternalSearchResponse> {
    return request<ExternalSearchResponse>(`/admin/search?q=${encodeURIComponent(query)}&limit=${limit}`);
  },

  async getSearchableSources(): Promise<{ sources: SearchableSource[]; total: number }> {
    return request<{ sources: SearchableSource[]; total: number }>('/admin/search/sources');
  },
};
